import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  TextField,
  CircularProgress,
  Autocomplete,
  MaskedInput,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "../../../components";
import { AddLayout } from "../../../layouts";
import {
  notification,
  PessoaService,
  EnderecoService,
  VeiculosService,
} from "../../../services";
import { PermisoesHelper, InputErros, value } from "../../../helpers";
import { responseErros, cacheApi, voltar, userLogs } from "../../../utils";
import { useCacheContext } from "../../../contexts/cache.context";
import { Veiculo } from "./entities";
import { veiculoValidator } from "./middlewares";
import { useImportContext } from "../../../contexts/import.context";

const permissoesHelper = new PermisoesHelper();
const pessoaService = new PessoaService();
const enderecoService = new EnderecoService();
const veiculosService = new VeiculosService();

const AddVeiculosView = ({ match }) => {
  const { id } = match.params;
  const [loading, setLoading] = useState(false);
  const { initialCollapses } = useImportContext();
  const [userLog, setUserLog] = useState({});
  const [proprietarioLista, setProprietarioLista] = useState([]);
  const [estados, setEstados] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const [veiculo, setVeiculo] = useState({});
  const inputProprietario = useRef();
  const { cacheDto, setCacheDto } = useCacheContext();
  permissoesHelper.validarPermisao("veiculos-visualizar");

  useEffect(() => {
    buscaEstados();
    if (id) {
      buscaVeiculo();
    }
  }, []);

  const buscaVeiculo = async () => {
    setLoading(true);
    const result = await veiculosService.getById(id);
    setLoading(false);
    if (!result.isAxiosError) {
      if (!result.data) {
        initialCollapses();
        voltar();
        return;
      }
      buscarRelacionamentoPessoas(result.data.intPessoaId);
      setVeiculo(result.data);
    } else {
      responseErros(result);
    }
  };

  function buscarRelacionamentoPessoas(intPessoaId) {
    const filtros = {
      id: intPessoaId,
    };
    buscarPessoas(filtros).then((pessoas) => {
      setProprietarioLista(pessoas);
    });
  }

  const handleSubmit = (indiceSelecionado) => {
    const veiculoDto = new Veiculo(veiculo);
    if (id) {
      atualizarVeiculo(veiculoDto);
    } else {
      cadastrarVeiculo(indiceSelecionado, veiculoDto);
    }
  };

  const cadastrarVeiculo = async (acaoCastro, veiculoDto) => {
    veiculoValidator
      .validate(veiculoDto, { abortEarly: false })
      .then(async () => {
        setLoading(true);
        const result = await veiculosService.cadastrar(veiculoDto);
        setLoading(false);
        if (!result.isAxiosError) {
          notification.cadastroSucesso();
          if (acaoCastro == 0) {
            initialCollapses();
            voltar();
          } else {
            setVeiculo({});
          }
        } else responseErros(result);
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  const atualizarVeiculo = async (veiculoDto) => {
    veiculoValidator
      .validate(veiculoDto, { abortEarly: false })
      .then(async () => {
        setLoading(true);
        const result = await veiculosService.atualizar(id, veiculoDto);
        setLoading(false);
        if (!result.isAxiosError) {
          notification.alteracaoSucesso();
          initialCollapses();
          voltar();
        } else {
          responseErros(result);
        }
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  const handleChangeAutocompleteUfLicenciamento = (value) => {
    setVeiculo({
      ...veiculo,
      ufLicenciamento: value ? value.sigla : undefined,
    });
  };

  const buscarPessoas = async (filtros) => {
    const filtro = {
      ...filtros,
      nonPaginated: true,
      ativado: true,
    };
    const res = await pessoaService.getAll(filtro);
    if (!res.isAxiosError) {
      return res.data;
    }
    responseErros(res);
    return [];
  };

  const onSearchChangeProprietario = async (event, inputRef) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const filtros = {
        nomeRazaoSocial: event.target?.value,
        apelidoFantasia: event.target?.value,
      };
      setLoadingAutoComplete(true);
      const pessoas = await buscarPessoas(filtros);
      setLoadingAutoComplete(false);
      setProprietarioLista(pessoas);
    }
  };

  const handleInputChange = (event) => {
    setInputErro([]);
    setVeiculo({
      ...veiculo,
      [event.target.name]: event.target.value || "",
    });
  };

  const handleInputChangeTipo = (event) => {
    setInputErro([]);
    let tipoRodado = veiculo.tipoRodado;
    if (event.target.value == "REBOQUE") {
      tipoRodado = "";
    }
    if (event.target.value) {
      setVeiculo({
        ...veiculo,
        tipo: event.target.value,
        tipoRodado: tipoRodado,
      });
    }
  };

  const handleInputChangePlaca = (event) => {
    setInputErro([]);
    setVeiculo({
      ...veiculo,
      placa: event.target.value ? event.target.value.toUpperCase() : "",
    });
  };

  const onChangeAutocomplete = (name, value) => {
    setInputErro([]);
    setVeiculo({
      ...veiculo,
      [name]: value ? value.id : undefined,
    });
  };

  const buscaEstados = async () => {
    const cacheBody = {
      timeMinutes: 60,
      apiFunction: enderecoService.getEstados(),
      dto: { cacheDto: cacheDto, setCacheDto: setCacheDto },
    };
    const res = await cacheApi(`${enderecoService.urlBase}/estados`, cacheBody);
    if (!res.isAxiosError) {
      setEstados(res.data);
    } else {
      responseErros(res);
    }
  };

  function valueAutoCompleteUfLicenciamento(ufSigla) {
    return estados.find((estado) => estado.sigla === ufSigla);
  }

  const handleCheckBoxVeiculoProprio = (event) => {
    let veiculoTemp = veiculo;
    if (event.target.checked) {
      veiculoTemp = {
        ...veiculoTemp,
        intPessoaId: null,
        rntcProprietario: null,
      };
    }
    setVeiculo({
      ...veiculoTemp,
      veiculoProprio: event.target.checked,
    });
  };

  async function onClickLog() {
    if ("userCreatedLog" in userLog) return;
    const logs = await userLogs(veiculo);
    setUserLog(logs);
  }

  return (
    <AddLayout
      id={id}
      onClickSalvar={handleSubmit}
      title={id ? "Editar veículo" : "Cadastrar veículo"}
      loading={loading}
      onClickLog={onClickLog}
      userLog={id ? userLog : null}
      showUpdate={permissoesHelper.temPermisao("veiculos-editar")}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={veiculo?.veiculoProprio ?? false}
                onClick={handleCheckBoxVeiculoProprio}
                color="primary"
                value={veiculo?.veiculoProprio ?? false}
                name="veiculoProprio"
              />
            }
            label="Veículo Próprio"
          />
        </Grid>
        <Grid item xs={4}>
          <Autocomplete
            id="intPessoaId"
            name="intPessoaId"
            options={proprietarioLista}
            disabled={veiculo?.veiculoProprio ?? false}
            autoHighlight
            getOptionLabel={(option) => {
              let codigoPessoa = option?.codigo ? `${option.codigo} - ` : "";
              let descricaoPessoa = codigoPessoa + `${option.nomeRazaoSocial}`;
              return option ? descricaoPessoa : "";
            }}
            loading={loadingAutoComplete}
            loadingText="Carregando"
            noOptionsText={
              document.getElementById("intPessoaId")?.value.length >= 1 &&
              !id ? (
                <Grid container spacing={1}>
                  <Grid item xs={12} fullWidth>
                    Nenhum proprietário encontrado
                  </Grid>
                </Grid>
              ) : (
                "Digite e pressione Enter"
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Proprietário"
                error={inputErros.get("intPessoaId")}
                required={true}
                variant={veiculo?.veiculoProprio ? "filled" : "outlined"}
                onKeyDown={(e) =>
                  onSearchChangeProprietario(e, inputProprietario)
                }
                inputRef={inputProprietario}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingAutoComplete ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            onChange={(_, newValue) =>
              onChangeAutocomplete("intPessoaId", newValue)
            }
            value={value.autoComplete(proprietarioLista, veiculo?.intPessoaId)}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            id="rntcProprietario"
            name="rntcProprietario"
            label="RNTC Proprietário"
            type="number"
            disabled={veiculo?.veiculoProprio ?? false}
            variant={veiculo?.veiculoProprio ? "filled" : "outlined"}
            error={inputErros?.get("rntcProprietario")}
            onChange={handleInputChange}
            value={value.text(veiculo?.rntcProprietario)}
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <MaskedInput
            id="placa"
            name="placa"
            label="Placa"
            margin="none"
            type="text"
            mask="aaa-9*99"
            onChange={handleInputChangePlaca}
            value={veiculo?.placa ?? ""}
            required
            error={inputErros.get("placa")}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={3}>
          <FormControl variant="outlined" margin="none" fullWidth>
            <Autocomplete
              required
              id="ufLicenciamento"
              name="ufLicenciamento"
              options={estados}
              noOptionsText="Sem opções"
              autoHighlight
              getOptionLabel={(option) =>
                option.sigla && option.nome
                  ? `${option.sigla} - ${option.nome}`
                  : ""
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="UF Licenciamento"
                  required
                  variant="outlined"
                  error={inputErros?.get("ufLicenciamento")}
                />
              )}
              onChange={(event, newValue) =>
                handleChangeAutocompleteUfLicenciamento(newValue)
              }
              value={valueAutoCompleteUfLicenciamento(veiculo?.ufLicenciamento)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <TextField
            id="renavam"
            name="renavam"
            label="Renavam"
            type="number"
            error={inputErros?.get("renavam")}
            onChange={handleInputChange}
            value={value.text(veiculo?.renavam)}
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <Select
            id="tipo"
            name="tipo"
            value={veiculo?.tipo ?? ""}
            label="Tipo"
            disableClearable
            onChange={handleInputChangeTipo}
            fullWidth
            error={inputErros?.get("tipo")}
            required
          >
            <MenuItem value="VEICULO">Veiculo</MenuItem>
            <MenuItem value="REBOQUE">Reboque</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={3}>
          <Select
            id="tipoRodado"
            name="tipoRodado"
            value={veiculo?.tipoRodado ?? ""}
            label="Tipo Rodado"
            disableClearable
            onChange={handleInputChange}
            fullWidth
            variant={veiculo?.tipo == "REBOQUE" ? "filled" : "outlined"}
            disabled={veiculo?.tipo == "REBOQUE" ? true : false}
            error={inputErros?.get("tipoRodado")}
            required={veiculo?.tipo == "REBOQUE" ? false : true}
          >
            <MenuItem value="Truck">Truck</MenuItem>
            <MenuItem value="Toco">Toco</MenuItem>
            <MenuItem value="CavaloMecanico">Cavalo Mecânico</MenuItem>
            <MenuItem value="Carreta">Carreta</MenuItem>
            <MenuItem value="VAN">VAN</MenuItem>
            <MenuItem value="Utilitario">Utilitário</MenuItem>
            <MenuItem value="Outro">Outro</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={3}>
          <Select
            id="tipoCarroceria"
            name="tipoCarroceria"
            value={veiculo?.tipoCarroceria ?? ""}
            label="Tipo Carroceria"
            disableClearable
            onChange={handleInputChange}
            fullWidth
            required
            error={inputErros?.get("tipoCarroceria")}
          >
            <MenuItem value="NaoAplicavel">Não Aplicável</MenuItem>
            <MenuItem value="Aberta">Aberta</MenuItem>
            <MenuItem value="FechadaBau">Fechada/Baú</MenuItem>
            <MenuItem value="Graneleira">Graneleira</MenuItem>
            <MenuItem value="PortaContainer">Porta Container</MenuItem>
            <MenuItem value="Sider">Sider</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={2}>
          <TextField
            id="tara"
            name="tara"
            label="Tara"
            type="number"
            error={inputErros?.get("tara")}
            onChange={handleInputChange}
            value={value.text(veiculo?.tara)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            id="capacidadeKg"
            name="capacidadeKg"
            label="Capacidade (Kg)"
            type="number"
            error={inputErros?.get("capacidadeKg")}
            onChange={handleInputChange}
            value={value.text(veiculo?.capacidadeKg)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            id="capacidadeM3"
            name="capacidadeM3"
            label="Capacidade (m3)"
            type="number"
            error={inputErros?.get("capacidadeM3")}
            onChange={handleInputChange}
            value={value.text(veiculo?.capacidadeM3)}
            fullWidth
            required
          />
        </Grid>
      </Grid>
    </AddLayout>
  );
};

export default AddVeiculosView;
